import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  LinearProgress,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogContent,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterHeader from "../../components/FilterHeader";
import { useEmployeeService } from "../../services/EmployeeService";
import EmployeeProfile from "./EmployeeProfile";

const columnDefs = [
  { key: "EmployeeId", label: "Emp Code", flex: 1 },
  { key: "Full_Name", label: "Full Name", flex: 2 },
  { key: "Mobile", label: "Mobile", flex: 1 },
  { key: "Official_Email_ID", label: "Email", flex: 2 },
  { key: "Business_Vertical_Desc", label: "Vertical", flex: 1 },
  { key: "Business_Sub_Vertical_Description", label: "Sub Vertical", flex: 2 },
  { key: "LocationName", label: "Location", flex: 2 },
  { key: "GeoRadius", label: "Geo Radius (km)", flex: 1 },
];

const ROW_HEIGHT = 32;

const AssignedLocationList = () => {
  const { fetchAssignedLocations } = useEmployeeService();
  const [filters, setFilters] = useState({
    VerticalDescriptions: "All",
    ProjectDescriptions: "All",
    Locations: "All",
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedEmpID, setSelectedEmpID] = useState(null);
  const [search, setSearch] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const listRef = useRef();

  const handleProfileOpen = (empID) => {
    setSelectedEmpID(empID);
    setProfileOpen(true);
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
    setSelectedEmpID(null);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAssignedLocations();
      setData(res);
    } catch (err) {
      console.error("Failed to fetch assigned locations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearch({});
  }, [filters]);

  const handleSearch = (key, value) => {
    setSearch((prev) => ({ ...prev, [key]: value }));
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchVertical =
        filters.VerticalDescriptions === "All" ||
        item.Business_Vertical_Desc === filters.VerticalDescriptions;
      const matchProject =
        filters.ProjectDescriptions === "All" ||
        item.Project_Description === filters.ProjectDescriptions;
      const matchLocation =
        filters.Locations === "All" ||
        item.Business_Sub_Vertical_Description === filters.Locations;

      const matchesSearch = columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        if (!term) return true;

        const value = item[key];
        return value?.toString().toLowerCase().includes(term);
      });

      return matchVertical && matchProject && matchLocation && matchesSearch;
    });
  }, [data, filters, search]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];

    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.875rem",
        }}
      >
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1,
            }}
          >
            {key === "EmployeeId" ? (
              <Typography
                variant="body2"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => handleProfileOpen(row.EmployeeId)}
              >
                {row[key]}
              </Typography>
            ) : (
              row[key]
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h2" sx={{ minWidth: 180 }}>
          Assigned Locations
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "flex-end",
            minWidth: "60%",
          }}
        >
          <FilterHeader filters={filters} setFilters={setFilters} />
        </Box>
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Box component={Paper}>
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) => handleSearch(key, e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>

          {filteredData.length > 0 ? (
            <Box sx={{ height: "70vh" }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    width={width}
                    itemSize={() => ROW_HEIGHT}
                    itemCount={filteredData.length}
                    itemKey={(index) => filteredData[index].MappingId || index}
                    ref={listRef}
                  >
                    {Row}
                  </List>
                )}
              </AutoSizer>
            </Box>
          ) : (
            <Box p={2} textAlign="center" color="text.secondary">
              No matching records found.
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={profileOpen}
        onClose={handleProfileClose}
        maxWidth={false} // disables fixed maxWidth
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "80%",
            maxWidth: "80%",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0 }}>
          <IconButton onClick={handleProfileClose}>
            <span style={{ fontSize: "1.2rem" }}>✕</span>
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 1, pb: 1, pt: 0 }}>
          <Box sx={{ display: "block", width: "100%", minWidth: 0 }}>
            <EmployeeProfile empID={selectedEmpID} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AssignedLocationList;
